import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode
} from 'react';
import { fetchOrganisations } from '../network/fetchOrganisations';
import { type Organisation } from '../models/organisation';
import axios from 'axios';
import { API_URL } from '../constants/environment';

interface UserContextType {
  availableOrganisations: Organisation[] | undefined;
  currentOrganisation: Organisation | undefined;
  setCurrentOrganisation: React.Dispatch<
    React.SetStateAction<Organisation | undefined>
  >;
  loading: boolean;
  loginUser: (orgId: string) => Promise<void>;
  logoutUser: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const [availableOrganisations, setAvailableOrganisations] = useState<
    Organisation[]
  >([]);
  const [currentOrganisation, setCurrentOrganisation] =
    useState<Organisation>();

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrgs = async () => {
      const result = await fetchOrganisations();
      const organisations = result?.data;

      if (organisations && organisations.length > 0) {
        setAvailableOrganisations(organisations);
        const defaultOrg = localStorage.getItem('defaultOrg');
        if (defaultOrg) {
          const currentOrg = organisations.find(
            (org) => org.id === Number(defaultOrg)
          );

          // if no matching org has been found in the application storage, then set the first one to be the default
          // and set it to also be the current one
          if (!currentOrg) {
            localStorage.setItem('defaultOrg', organisations[0].id.toString());
            setCurrentOrganisation(organisations[0]);
          } else {
            // an org has been found that matches whatever the user set as a default org.
            setCurrentOrganisation(currentOrg);
          }
        } else {
          // if no default org has been set by the user. Set it to be the first one
          setCurrentOrganisation(organisations[0]);
        }
      }
      setLoading(false);
    };

    fetchOrgs();
  }, []);

  /**
   * Sets the current organisations
   */
  useEffect(() => {
    const fetchUser = async () => {
      if (currentOrganisation) {
        // only try to login the user if they do not have a magic token
        if (!new URLSearchParams(location.search).has('token')) {
          await loginUser(currentOrganisation.id.toString());
        }
        setCurrentOrganisation(currentOrganisation);
      }
    };

    fetchUser();
  }, [currentOrganisation]);

  const loginUser = async (orgId: string) => {
    await axios.post(`/api/v1/customer/organisations/${orgId}/set_current`);
  };

  const logoutUser = async () => {
    await axios.delete('/api/v1/authentication/token');
    window.location.replace(`${API_URL}/docs`);
  };

  return (
    <UserContext.Provider
      value={{
        availableOrganisations,
        currentOrganisation,
        setCurrentOrganisation,
        loading,
        loginUser,
        logoutUser
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
